import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarDays, Users, Sparkles, ShieldCheck, Smartphone, Check, Search, MapPin, Star, ChevronRight, Store, LocateFixed } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const QUICK = ["Facials", "Nails", "Massage", "Waxing", "Hair", "Lashes & Brows"];
const img = (u) => (!u ? null : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

export const LogoMark = ({ dark = false, className = "h-9 w-9" }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true" data-testid="logo-mark">
    <rect x="2" y="6" width="36" height="32" rx="9" fill={dark ? "#D4A373" : "#1c1917"} />
    <rect x="11" y="2" width="4" height="9" rx="2" fill={dark ? "#D4A373" : "#1c1917"} />
    <rect x="25" y="2" width="4" height="9" rx="2" fill={dark ? "#D4A373" : "#1c1917"} />
    <path d="M2 15h36" stroke={dark ? "#1c1917" : "#D4A373"} strokeOpacity=".35" strokeWidth="2" />
    <path d="M12.5 26.5l5 5 10-11" fill="none" stroke={dark ? "#1c1917" : "#D4A373"} strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Logo = ({ dark = false }) => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <LogoMark dark={dark} />
    <span className="font-extrabold text-base sm:text-lg whitespace-nowrap tracking-tight leading-none">Get <span className={dark ? "text-[#D4A373]" : "text-[#B07E4F]"} data-testid="logo-booked">Booked</span> In</span>
  </div>
);

function SalonCard({ b }) {
  const accent = b.accent || "#B07E4F";
  const cover = img(b.cover_url);
  return (
    <Link to={`/${b.slug}`} data-testid={`directory-salon-${b.slug}`} className="group min-w-0 bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-md hover:border-stone-900 transition-[box-shadow,border-color] overflow-hidden flex flex-col">
      {cover && <div className="h-36 overflow-hidden"><img src={cover} alt="" className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" /></div>}
      <div className="p-4 sm:p-5 flex gap-4 min-w-0">
      <div className="h-16 w-16 rounded-2xl shrink-0 overflow-hidden flex items-center justify-center text-white font-bold text-xl" style={{ background: accent }}>
        {b.logo_url ? <img src={img(b.logo_url)} alt="" className="h-full w-full object-cover" /> : b.name[0]}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold leading-tight">{b.name}</div>
        {b.tagline && <div className="text-xs font-semibold uppercase tracking-wider mt-0.5" style={{ color: accent }}>{b.tagline}</div>}
        {b.address && <div className="text-sm text-stone-500 mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{b.address}</span></div>}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {b.distance_miles != null && <span data-testid={`directory-distance-${b.slug}`} className="text-xs font-semibold text-stone-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{b.distance_miles} mi away</span>}
          {b.review_count > 0 && <span className="text-xs flex items-center gap-1 font-semibold"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{b.rating} <span className="text-stone-400 font-normal">({b.review_count})</span></span>}
          {b.categories.slice(0, 4).map((c) => <span key={c} className="text-[11px] bg-stone-100 text-stone-600 rounded-full px-2 py-0.5">{c}</span>)}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-stone-300 self-center group-hover:text-stone-900 transition-colors" />
      </div>
    </Link>
  );
}

function ClientSearch() {
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [results, setResults] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [widened, setWidened] = useState(false);
  const [locating, setLocating] = useState(false);
  const run = useCallback(async (term = q, location = loc, coords = null) => {
    const { data } = await api.get("/public/businesses", { params: { q: term, location, ...(coords || {}) } });
    setResults(data.results); setOrigin(data.origin); setWidened(data.widened);
  }, [q, loc]);
  useEffect(() => { api.get("/public/businesses").then((r) => setResults(r.data.results)).catch(() => setResults([])); }, []);
  const submit = (e) => { e.preventDefault(); run(); };
  const nearMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLoc(""); run(q, "", { lat: p.coords.latitude, lng: p.coords.longitude }).finally(() => setLocating(false)); },
      () => setLocating(false), { timeout: 10000 });
  };

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 lg:pt-14">
      <div className="animate-fade-up max-w-3xl">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500 bg-white border border-stone-200 rounded-full px-3 py-1"><Sparkles className="h-3.5 w-3.5 text-[#C59B72]" /> Salons, clinics, studios & any business that takes bookings</span>
        <h1 className="mt-5 font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Haircuts to health checks.<br /><span className="text-[#B07E4F]">Get booked in.</span></h1>
        <p className="mt-4 text-base sm:text-lg text-stone-600 max-w-xl">Salons, clinics, trainers, tutors, studios and more. Search local businesses, see real prices and book online in under a minute.</p>
      </div>
      <form onSubmit={submit} className="mt-7 bg-white rounded-3xl sm:rounded-full border border-stone-200 shadow-lg p-2 flex flex-col sm:flex-row gap-2 max-w-3xl" data-testid="directory-search-form">
        <label className="flex items-center gap-2 sm:flex-1 shrink-0 px-4 h-12"><Search className="h-4 w-4 text-stone-400" /><input data-testid="directory-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Service or business name" className="w-full outline-none bg-transparent text-sm" /></label>
        <label className="flex items-center gap-2 sm:flex-1 shrink-0 px-4 h-12 border-t sm:border-t-0 sm:border-l border-stone-200"><MapPin className="h-4 w-4 text-stone-400" /><input data-testid="directory-location-input" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Town or postcode" className="w-full outline-none bg-transparent text-sm" />
          <button type="button" data-testid="directory-near-me" onClick={nearMe} title="Use my location" className="shrink-0 text-stone-500 hover:text-stone-900"><LocateFixed className={`h-4 w-4 ${locating ? "animate-pulse" : ""}`} /></button></label>
        <Button type="submit" data-testid="directory-search-button" className="rounded-full h-12 px-7 bg-stone-900 hover:bg-stone-800">Search</Button>
      </form>
      <div className="mt-4 flex flex-wrap gap-2">
        {QUICK.map((c) => <button key={c} data-testid={`directory-quick-${c}`} onClick={() => { setQ(c); run(c, loc); }} className="text-sm px-3.5 py-1.5 rounded-full bg-white border border-stone-200 hover:border-stone-900 transition-colors">{c}</button>)}
      </div>
      <div className="mt-10" data-testid="directory-results">
        <h2 className="font-semibold text-lg mb-4" data-testid="directory-heading">{origin ? (widened ? `No salons within 30 miles of ${origin.label} yet — nearest further away` : `Nearest to ${origin.label}`) : q || loc ? "Search results" : "Businesses on Get Booked In"}</h2>
        {results === null ? <p className="text-sm text-stone-500">Loading…</p> : results.length === 0 ? (
          <p className="text-sm text-stone-500 bg-white border border-stone-200 rounded-2xl p-5" data-testid="directory-empty">No salons found. Try a different treatment or town.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{results.map((b) => <SalonCard key={b.slug} b={b} />)}</div>
        )}
      </div>
    </section>
  );
}

function ForBusinesses() {
  return (
    <section id="for-businesses" className="mt-24 bg-stone-900 text-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#D4A373] flex items-center gap-2"><Store className="h-3.5 w-3.5" />For salons & clinics</span>
          <h2 className="mt-4 font-extrabold text-3xl sm:text-4xl leading-tight">Keep your chair full,<br /><span className="text-[#D4A373]">skip the admin.</span></h2>
          <p className="mt-4 text-stone-300 max-w-md">Your own booking page at getbookedin.co.uk/your-salon, a smart calendar, client records, deposits and reminders. Just £19.99/month.</p>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-stone-300">
            {["2 staff included", "Your own booking link", "Deposits & no-show fees", "Upload your price list"].map((f) => <span key={f} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-400" />{f}</span>)}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register"><Button size="lg" data-testid="hero-cta-button" className="rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#C29263] h-12 px-7 text-base">Start 14-day free trial</Button></Link>
            <span className="text-sm text-stone-400">No card required</span>
          </div>
        </div>
        <div className="rounded-3xl overflow-hidden border border-stone-700 aspect-[4/3]"><img src={HERO} alt="Salon interior" className="h-full w-full object-cover" /></div>
      </div>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: CalendarDays, t: "Smart calendar", d: "Day & week views, staff columns and no double bookings." },
          { icon: Smartphone, t: "Mobile booking", d: "A beautiful page in your own colours that clients book from any phone." },
          { icon: Users, t: "Client records", d: "History, notes, consent forms and no-show tracking." },
          { icon: ShieldCheck, t: "Secure & private", d: "Your business data is kept separate and only you can see it." },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl border border-stone-700 bg-stone-800/60 p-6">
            <div className="h-11 w-11 rounded-xl bg-stone-700 flex items-center justify-center"><c.icon className="h-5 w-5 text-[#D4A373]" /></div>
            <h3 className="mt-4 font-semibold text-lg">{c.t}</h3>
            <p className="mt-1.5 text-sm text-stone-400 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user && user.role) navigate(user.role === "admin" ? "/admin" : "/dashboard"); }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-stone-900">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-3">
        <Logo />
        <div className="flex items-center gap-1 sm:gap-2">
          <a href="#for-businesses" data-testid="nav-for-businesses" className="hidden sm:inline text-sm font-medium px-3">For businesses</a>
          <Link to="/login"><Button variant="ghost" data-testid="nav-login-button" className="rounded-full px-3 text-sm">Sign in</Button></Link>
          <Link to="/register"><Button data-testid="nav-register-button" className="rounded-full bg-stone-900 hover:bg-stone-800 px-3 sm:px-4 text-sm"><span className="sm:hidden">Join</span><span className="hidden sm:inline">List your business</span></Button></Link>
        </div>
      </header>
      <ClientSearch />
      <ForBusinesses />
      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-8 text-sm text-stone-500 flex flex-wrap justify-between gap-2">
        <span>© {new Date().getFullYear()} Get Booked In · getbookedin.co.uk</span>
        <Link to="/register" className="underline">List your business</Link>
      </footer>
    </div>
  );
}
